import type { ComponentChildren } from 'preact';
import { useState } from 'preact/hooks';
import { Popover } from './Popover';
import { useWide } from '../shell/hooks';

// The one drop-down (consistency pass, R4, Kristi Nov 1): the Secondary pill with ▾, transparent,
// 32 px on a laptop and 44 on touch. Its menu marks the current choice with ✓ and opens as a
// bottom sheet on a phone. Labels say what it is: an action names its verb ("Move ▾"), a setting
// names itself and its value ("Sort: Reading order ▾"), a value in a row shows only the value
// ("Paper ▾"). An empty value is dashed and says what's missing ("No read type ▾").

export interface MenuChoice<T> {
  value: T;
  label: ComponentChildren;
  /** A quiet note at the right of the row ("asks why"). */
  note?: string;
  /** Starts a new group with a line above it. */
  divided?: boolean;
  /** A small-capitals heading above this item. */
  heading?: string;
  disabled?: boolean;
}

/** The menu's rows: ✓ before the current one (R4 D3). */
export function MenuList<T>({ choices, current, onPick, extra }: { choices: MenuChoice<T>[]; current?: T | null; onPick: (v: T) => void; extra?: ComponentChildren }) {
  return (
    <div role="menu" class="menu">
      {choices.map((c, i) => (
        <>
          {c.heading ? (
            <span key={`h${i}`} class="menu-head">
              {c.heading}
            </span>
          ) : null}
          <button
            key={i}
            type="button"
            role="menuitemradio"
            aria-checked={current !== undefined && c.value === current}
            disabled={c.disabled}
            class={`menu-item${c.divided ? ' divided' : ''}${current !== undefined && c.value === current ? ' current' : ''}`}
            onClick={() => onPick(c.value)}
          >
            <span class="menu-check" aria-hidden="true">
              {current !== undefined && c.value === current ? '✓' : ''}
            </span>
            <span class="menu-label">{c.label}</span>
            {c.note ? <span class="menu-item-note">{c.note}</span> : null}
          </button>
        </>
      ))}
      {extra}
    </div>
  );
}

export function PillMenu<T>({
  label,
  ariaLabel,
  sheetTitle,
  choices,
  current,
  onPick,
  empty,
  bar,
  extra,
  class: cls = '',
  title,
}: {
  /** What the pill shows, without the ▾. */
  label: ComponentChildren;
  ariaLabel: string;
  sheetTitle?: string;
  choices: MenuChoice<T>[];
  current?: T | null;
  onPick: (v: T) => void;
  /** An empty value: dashed (R4 D2). */
  empty?: boolean;
  /** A read-type colour bar before the value. */
  bar?: string;
  extra?: ComponentChildren;
  class?: string;
  title?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <span class="pop-host">
      <button
        type="button"
        class={`btn-secondary pill-menu${empty ? ' empty' : ''}${cls ? ` ${cls}` : ''}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={ariaLabel}
        title={title}
        onClick={() => setOpen(!open)}
      >
        {bar ? <span class="pill-menu-bar" style={{ background: bar }} aria-hidden="true" /> : null}
        {label} <span aria-hidden="true">▾</span>
      </button>
      <Popover open={open} onClose={() => setOpen(false)} label={ariaLabel} sheetTitle={sheetTitle ?? ariaLabel}>
        <MenuList
          choices={choices}
          current={current}
          extra={extra}
          onPick={(v) => {
            setOpen(false);
            onPick(v);
          }}
        />
      </Popover>
    </span>
  );
}

/**
 * The same pill when the value is typed or pasted rather than picked (filters review, Kristi Nov 2):
 * a dashed "No Kindle ID ▾" in a row opens a small box beside it (a bottom sheet on a phone) instead
 * of a menu. `children` get `close`, to call once saved.
 */
export function PillPanel({
  label,
  ariaLabel,
  sheetTitle,
  empty,
  children,
  class: cls = '',
}: {
  label: ComponentChildren;
  ariaLabel: string;
  sheetTitle?: string;
  /** An empty value: dashed (R4 D2). */
  empty?: boolean;
  children: (close: () => void) => ComponentChildren;
  class?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <span class="pop-host">
      <button
        type="button"
        class={`btn-secondary pill-menu${empty ? ' empty' : ''}${cls ? ` ${cls}` : ''}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen(!open)}
      >
        {label} <span aria-hidden="true">{open ? '▴' : '▾'}</span>
      </button>
      <Popover open={open} onClose={() => setOpen(false)} label={ariaLabel} sheetTitle={sheetTitle ?? ariaLabel}>
        <div class="pill-panel">{children(() => setOpen(false))}</div>
      </Popover>
    </span>
  );
}

/**
 * A setting's pill: "Sort: Reading order ▾". On a phone it shows only its value, "Reading order ▾", so
 * Filters ▾, Sort and Select fit one line (R9 M7, Kristi Nov 2); its sheet says "Sort by" and
 * screen readers still hear "Sort: Reading order".
 */
export function SettingMenu<T extends string>({ name, choices, value, onChange, narrow: narrowProp }: { name: string; choices: [T, string][]; value: T; onChange: (v: T) => void; narrow?: boolean }) {
  const wide = useWide();
  const narrow = narrowProp ?? !wide;
  const now = choices.find(([v]) => v === value)?.[1] ?? '';
  return (
    <PillMenu
      label={
        narrow ? (
          now
        ) : (
          <>
            {name}: <span class="pill-menu-value">{now}</span>
          </>
        )
      }
      ariaLabel={`${name}: ${now}`}
      sheetTitle={`${name} by`}
      choices={choices.map(([v, l]) => ({ value: v, label: l }))}
      current={value}
      onPick={onChange}
    />
  );
}
